import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import {
  SITE_LOCALES,
  pathForLocale,
  type PageContent,
  type SiteLocale,
} from "../content/index.js";
import { LanguagePicker } from "../sections/language-picker.js";
import { whatsappHref } from "../shared/links.js";
import { KuoriLogo } from "../shared/logo.js";
import { isDark, surfaceColorAt } from "../shared/surface.js";
import type { MenuCopy, MenuItem } from "./content.js";
import { MenuIcon } from "./icons.js";

export function sectionAt(
  tops: readonly (readonly [string, number])[],
  line: number,
): string | null {
  let found: string | null = null;
  for (const [id, top] of tops) {
    if (top <= line) {
      found = id;
    }
  }
  return found;
}

function sectionIds(menu: MenuCopy): string[] {
  return [...menu.items, ...menu.links]
    .filter((item) => !item.external)
    .map((item) => item.href.replace(/^#/, ""));
}

interface EntryProps {
  readonly item: MenuItem;
  readonly current: boolean;
  readonly index: number;
  onGo(href: string): void;
}

function Entry({ item, current, index, onGo }: EntryProps) {
  return (
    <li class="vnav-entry" style={{ "--i": index }}>
      <a
        class={`${item.body ? "vnav-item" : "vnav-row"}${current ? " is-current" : ""}`}
        href={item.href}
        aria-current={current ? "location" : undefined}
        {...(item.external
          ? { target: "_blank", rel: "noopener" }
          : {
              onClick: (event: MouseEvent) => {
                event.preventDefault();
                onGo(item.href);
              },
            })}
      >
        <span class="vnav-icon">
          <MenuIcon name={item.icon} size={22} />
        </span>
        <span class="vnav-text">
          <span class="vnav-title">{item.title}</span>
          {item.body ? <span class="vnav-body">{item.body}</span> : null}
        </span>
        <span class="vnav-arrow">
          <MenuIcon name={item.external ? "external" : "arrow"} size={18} />
        </span>
      </a>
    </li>
  );
}

export interface V2NavProps {
  readonly content: PageContent;
  readonly menu: MenuCopy;
  readonly locale?: SiteLocale;
}

const SHORT: Record<SiteLocale, string> = { "pt-BR": "PT", en: "EN", es: "ES" };

export function V2Nav({ content, menu, locale }: V2NavProps) {
  const [solid, setSolid] = useState(false);
  const [onDark, setOnDark] = useState(false);
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(false);
  const [current, setCurrent] = useState<string | null>(null);
  const pill = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const burger = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const pointer = useRef("");
  const whatsapp = whatsappHref(content);

  useEffect(() => {
    const ids = sectionIds(menu);
    let queued = false;
    const wide =
      typeof window.matchMedia === "function" ? window.matchMedia("(min-width: 901px)") : null;
    const read = () => {
      queued = false;
      if (wide?.matches) {
        setOpen(false);
      }
      setSolid(window.scrollY > 40);
      const row = bar.current;
      if (row) {
        const box = row.getBoundingClientRect();
        const colour = surfaceColorAt(
          Math.round(box.left + box.width / 2),
          Math.round(box.bottom + 24),
          pill.current,
        );
        setOnDark(colour !== null && isDark(colour));
      }
      const tops = ids.flatMap((id) => {
        const element = document.getElementById(id);
        return element ? [[id, element.getBoundingClientRect().top] as const] : [];
      });
      setCurrent(sectionAt(tops, window.innerHeight * 0.35));
    };
    const schedule = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(read);
      }
    };
    read();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [menu]);

  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    if (open) {
      sheet.current?.focus({ preventScroll: true });
    }
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open && !drop) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDrop(false);
        if (open) {
          setOpen(false);
          burger.current?.focus();
        }
      }
    };
    const onPointer = (event: PointerEvent) => {
      if (drop && pill.current && !pill.current.contains(event.target as Node)) {
        setDrop(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open, drop]);

  const go = (href: string) => {
    setOpen(false);
    setDrop(false);
    document.documentElement.style.overflow = "";
    requestAnimationFrame(() => {
      document.querySelector(href)?.scrollIntoView({ block: "start" });
      history.replaceState(null, "", href);
    });
  };

  const isCurrent = (item: MenuItem) => item.href === `#${current ?? ""}`;
  const dark = onDark && !open && !drop;
  const pillClass = `vnav-pill${solid ? " is-solid" : ""}${open ? " is-open" : ""}`;

  return (
    <div class={open ? "vnav is-open" : "vnav"}>
      {open ? <div class="vnav-scrim" onClick={() => setOpen(false)} /> : null}
      <div class="vnav-dock">
        <div ref={pill} class={pillClass} data-theme={dark ? "dark" : undefined}>
          <div class="vnav-bar" ref={bar}>
            <a class="vnav-home" href="#top" aria-label="Kuori">
              <KuoriLogo height={20} />
            </a>
            <nav class="vnav-links" aria-label={content.nav.sections}>
              <div
                class="vnav-drop"
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse") {
                    setDrop(true);
                  }
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType === "mouse") {
                    setDrop(false);
                  }
                }}
              >
                <button
                  type="button"
                  class={drop ? "vnav-link is-active" : "vnav-link"}
                  aria-expanded={drop}
                  aria-controls="vnav-product"
                  onPointerDown={(event) => {
                    pointer.current = event.pointerType;
                  }}
                  onClick={() => {
                    const mouse = pointer.current === "mouse";
                    pointer.current = "";
                    setDrop((value) => (mouse ? true : !value));
                  }}
                >
                  {menu.product}
                  <span class="vnav-chevron">
                    <MenuIcon name="chevron" size={14} />
                  </span>
                </button>
                <div class={drop ? "vnav-panel is-open" : "vnav-panel"} id="vnav-product">
                  <ul class="vnav-grid">
                    {menu.items.map((item, index) => (
                      <Entry
                        key={item.href}
                        item={item}
                        index={index}
                        current={isCurrent(item)}
                        onGo={go}
                      />
                    ))}
                  </ul>
                </div>
              </div>
              {menu.links.map((item) =>
                item.external ? (
                  <a
                    key={item.href}
                    class="vnav-link vnav-out"
                    href={item.href}
                    target="_blank"
                    rel="noopener"
                  >
                    <MenuIcon name={item.icon} size={16} />
                    {item.title}
                    <span class="vnav-out-arrow">
                      <MenuIcon name="external" size={14} />
                    </span>
                  </a>
                ) : (
                  <a
                    key={item.href}
                    class={isCurrent(item) ? "vnav-link is-current" : "vnav-link"}
                    href={item.href}
                    onClick={(event) => {
                      event.preventDefault();
                      go(item.href);
                    }}
                  >
                    {item.title}
                  </a>
                ),
              )}
            </nav>
            <div class="vnav-tail">
              {locale ? <LanguagePicker content={content} locale={locale} /> : null}
              <a class="vnav-start" href={menu.startHref ?? whatsapp}>
                {menu.start}
                <span class="vnav-start-arrow">
                  <MenuIcon name="arrow" size={16} />
                </span>
              </a>
              <button
                ref={burger}
                type="button"
                class="vnav-burger"
                aria-label={open ? content.nav.close : content.nav.menu}
                aria-expanded={open}
                aria-controls="vnav-menu"
                onClick={() => setOpen((value) => !value)}
              >
                <MenuIcon name={open ? "close" : "burger"} size={22} />
              </button>
            </div>
          </div>
          <div class="vnav-menu" id="vnav-menu" inert={!open} data-testid="vnav-menu">
            <div class="vnav-sheet" ref={sheet} tabIndex={-1}>
              <p class="vnav-label">{menu.product}</p>
              <ul class="vnav-list">
                {menu.items.map((item, index) => (
                  <Entry
                    key={item.href}
                    item={item}
                    index={index}
                    current={isCurrent(item)}
                    onGo={go}
                  />
                ))}
              </ul>
              <p class="vnav-label">{menu.explore}</p>
              <ul class="vnav-list">
                {menu.links.map((item, index) => (
                  <Entry
                    key={item.href}
                    item={item}
                    index={index + menu.items.length}
                    current={isCurrent(item)}
                    onGo={go}
                  />
                ))}
              </ul>
              <div class="vnav-foot">
                {locale ? (
                  <div class="vnav-langs" role="group" aria-label={content.nav.language}>
                    {SITE_LOCALES.map((option) => (
                      <a
                        key={option}
                        href={pathForLocale(option)}
                        class={option === locale ? "is-current" : undefined}
                        hreflang={option}
                        aria-current={option === locale ? "page" : undefined}
                      >
                        {SHORT[option]}
                      </a>
                    ))}
                  </div>
                ) : null}
                <a class="vnav-talk" href={whatsapp}>
                  {menu.talk}
                  <MenuIcon name="arrow" size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
